import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { post } from '../helper/helper_api';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import {
    FaMapMarkerAlt, FaEnvelope, FaPhoneAlt,
    FaShieldAlt, FaLinkedinIn, FaTwitter, FaInstagram, FaFacebookF,
    FaPaperPlane, FaCheckCircle
} from 'react-icons/fa';
import HomeVideo from '../assets/images/check-video.mp4';

const CONTACT_INFO = [
    {
        icon: <FaMapMarkerAlt />,
        label: 'Our Office',
        value: 'FF-24, Al Sayegh Building, Port Saeed, Dubai, UAE',
        color: '#3B82F6',
    },
    {
        icon: <FaEnvelope />,
        label: 'Email Us',
        value: 'info@cyberfortify.tech',
        href: 'mailto:info@cyberfortify.tech',
        color: '#10B981',
    },
    {
        icon: <FaPhoneAlt />,
        label: 'Call Us',
        value: '+971-501227432',
        href: 'tel:+971501227432',
        color: '#A78BFA',
    },
];

const SERVICES = [
    'Network Penetration Testing',
    'Web Application Penetration Testing',
    'VAPT (Vulnerability Assessment)',
    'Mobile App Penetration Testing',
    'API Penetration Testing',
    'Managed Security Services',
    'Security Operations Centre (SOC)',
    'ISO 27001 Consulting',
    'ADHICS Compliance',
    'Annual Security Program',
];

const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay },
    viewport: { once: true },
});

export default function Contact() {
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => { window.scrollTo(0, 0); }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.name || !formData.email || !formData.message) {
            toast.error('Please fill in all required fields.');
            return;
        }
        setLoading(true);
        post('/contact', formData)
            .then((res) => {
                setLoading(false);
                if (res?.statusCode === 200) {
                    toast.success(res?.message || 'Message sent successfully!');
                    setSubmitted(true);
                    setFormData({ name: '', email: '', phone: '', service: '', message: '' });
                    setTimeout(() => setSubmitted(false), 5000);
                } else {
                    toast.error(res?.error || 'Something went wrong.');
                }
            })
            .catch(() => {
                setLoading(false);
                toast.error('Something went wrong. Please try again.');
            });
    };

    return (
        <>
            {/* ── HERO BANNER ── */}
            <section className="ct-hero">
                <video autoPlay loop muted playsInline className="ct-hero-video">
                    <source src={HomeVideo} type="video/mp4" />
                </video>
                <div className="ct-hero-overlay" />
                <div className="ct-hero-glow ct-hero-glow-l" />
                <div className="ct-hero-glow ct-hero-glow-r" />
                <motion.div {...fadeUp(0)} className="ct-hero-content">
                    <span className="ct-hero-badge">
                        <FaShieldAlt className="ct-hero-badge-icon" /> GET IN TOUCH
                    </span>
                    <h1 className="ct-hero-title">
                        Contact <span className="ct-hero-accent">Us</span>
                    </h1>
                    <p className="ct-hero-sub">
                        Ready to secure your digital assets? Our team of certified experts is available 24/7.
                    </p>
                    <nav aria-label="breadcrumb">
                        <ol className="ct-breadcrumb">
                            <li><Link to="/" className="ct-bc-link">Home</Link></li>
                            <li className="ct-bc-sep">›</li>
                            <li className="ct-bc-active">Contact Us</li>
                        </ol>
                    </nav>
                </motion.div>
            </section>

            {/* ── INFO CARDS ── */}
            <section className="ct-info-section">
                <div className="ct-info-top-line" />
                <div className="container">
                    <div className="row g-4 justify-content-center">
                        {CONTACT_INFO.map((item, i) => (
                            <div key={i} className="col-lg-4 col-md-6">
                                <motion.div {...fadeUp(i * 0.12)} className="ct-info-card">
                                    <div className="ct-info-card-top" style={{ background: `linear-gradient(90deg, ${item.color}, transparent)` }} />
                                    <div className="ct-info-icon-wrap" style={{ background: `${item.color}18`, border: `1px solid ${item.color}33` }}>
                                        <span className="ct-info-icon" style={{ color: item.color }}>{item.icon}</span>
                                    </div>
                                    <h4 className="ct-info-label">{item.label}</h4>
                                    {item.href
                                        ? <a href={item.href} className="ct-info-value ct-info-link">{item.value}</a>
                                        : <p className="ct-info-value">{item.value}</p>
                                    }
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── MAIN CONTACT SECTION ── */}
            <section className="ct-main-section">
                <div className="ct-main-glow-l" />
                <div className="ct-main-glow-r" />
                <div className="container">
                    <div className="row g-5 align-items-start">

                        {/* Left — info panel */}
                        <div className="col-lg-5">
                            <motion.div {...fadeUp(0)}>
                                <span className="ct-section-badge">REACH OUT</span>
                                <h2 className="ct-section-heading">
                                    Let's Secure Your<br />
                                    <span className="ct-section-accent">Business Together</span>
                                </h2>
                                <div className="ct-section-divider" />
                                <p className="ct-section-para">
                                    For customised cybersecurity solutions that drive security and confidence through innovation and expertise. Our certified consultants handle Penetration Testing, Compliance, SOC, and much more.
                                </p>

                                {/* Why contact us bullets */}
                                <div className="ct-bullets">
                                    {[
                                        'Free initial security consultation',
                                        'Response within 24 hours guaranteed',
                                        'Certified CREST, CEH, OSCP experts',
                                        'Tailored solutions for your industry',
                                    ].map((b, i) => (
                                        <div key={i} className="ct-bullet-item">
                                            <FaCheckCircle className="ct-bullet-icon" />
                                            <span className="ct-bullet-text">{b}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Social links */}
                                <div className="ct-social-row">
                                    <span className="ct-social-label">Follow Us</span>
                                    <div className="ct-social-links">
                                        {[
                                            { icon: <FaLinkedinIn />, href: '#' },
                                            { icon: <FaTwitter />, href: '#' },
                                            { icon: <FaInstagram />, href: '#' },
                                            { icon: <FaFacebookF />, href: '#' },
                                        ].map((s, i) => (
                                            <a key={i} href={s.href} className="ct-social-btn">{s.icon}</a>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        </div>

                        {/* Right — form */}
                        <div className="col-lg-7">
                            <motion.div {...fadeUp(0.15)} className="ct-form-card">
                                <div className="ct-form-card-top-line" />
                                <h3 className="ct-form-title">Send Us a Message</h3>
                                <p className="ct-form-sub">Fill in the form below and our team will get back to you within 24 hours.</p>

                                {submitted ? (
                                    <div className="ct-success-box">
                                        <FaCheckCircle className="ct-success-icon" />
                                        <h4 className="ct-success-title">Message Sent!</h4>
                                        <p className="ct-success-text">Thank you for reaching out. We will contact you shortly.</p>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="ct-form">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <div className="ct-field-wrap">
                                                    <label className="ct-label">Full Name <span className="ct-required">*</span></label>
                                                    <input
                                                        type="text" name="name" value={formData.name}
                                                        onChange={handleChange} placeholder="John Doe"
                                                        className="ct-input" required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="ct-field-wrap">
                                                    <label className="ct-label">Email Address <span className="ct-required">*</span></label>
                                                    <input
                                                        type="email" name="email" value={formData.email}
                                                        onChange={handleChange} placeholder="john@company.com"
                                                        className="ct-input" required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="ct-field-wrap">
                                                    <label className="ct-label">Phone Number</label>
                                                    <input
                                                        type="tel" name="phone" value={formData.phone}
                                                        onChange={handleChange} placeholder="+971 50 123 4567"
                                                        className="ct-input"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="ct-field-wrap">
                                                    <label className="ct-label">Service Required</label>
                                                    <select name="service" value={formData.service} onChange={handleChange} className="ct-input ct-select">
                                                        <option value="">Select a Service</option>
                                                        {SERVICES.map((s) => (
                                                            <option key={s} value={s}>{s}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <div className="ct-field-wrap">
                                                    <label className="ct-label">Message <span className="ct-required">*</span></label>
                                                    <textarea
                                                        name="message" value={formData.message}
                                                        onChange={handleChange}
                                                        placeholder="Tell us about your security requirements..."
                                                        className="ct-input ct-textarea" rows={5} required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <button type="submit" className="ct-submit-btn" disabled={loading}>
                                                    {loading ? (
                                                        <span className="ct-submit-loading">Sending...</span>
                                                    ) : (
                                                        <>
                                                            <FaPaperPlane className="ct-submit-icon" />
                                                            Send Message
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                )}
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── MAP ── */}
            <section className="ct-map-section">
                <div className="container">
                    <motion.div {...fadeUp(0)} className="ct-map-wrap">
                        <div className="ct-map-header">
                            <FaMapMarkerAlt className="ct-map-header-icon" />
                            <span className="ct-map-header-text">Our Dubai Office</span>
                        </div>
                        <iframe
                            className="ct-map-iframe"
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d29550169.802498687!2d44.76129718375484!3d25.297535361859616!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5deefa716dfb%3A0x457f02604d9940a!2sAl%20Sayegh%20Building!5e0!3m2!1sen!2sin!4v1728109484054!5m2!1sen!2sin"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Cyber Fortify Office Location"
                        />
                    </motion.div>
                </div>
            </section>
        </>
    );
}
